import type { TBaseResponse } from "@/models/common.model"
import type { ParticipantCreation, ParticipantList } from "@/models/participant.model"
import type { HttpClient } from "@/services/http-common"
import type { TSyncEventsSummary } from "@/types/sync"
import { tryCatch } from "@/lib/try-catch"
import ApiServiceFactory from "@/services/api-service-factory"

export class ParticipantsService {
  private httpClient: HttpClient
  private readonly baseUrl: string = "/participant"

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient
  }

  async create(participant: ParticipantCreation) {
    await this.httpClient.post(`${this.baseUrl}/create`, participant)
  }

  async getAll() {
    try {
      const response = await tryCatch(this.httpClient.get<TBaseResponse<ParticipantList[]>>(`${this.baseUrl}/get-all`))
      return response
    }
    catch (error) {
      console.error("Error al obtener los participantes:", error)
      throw error
    }
  }

  async getParticipantsByDiscipline(discipline: string, functionCodes: string[]) {
    try {
      const queryParams = new URLSearchParams()
      functionCodes.forEach(code => queryParams.append("functionCodes", code))

      const response = await this.httpClient.get<TBaseResponse<ParticipantList[]>>(`${this.baseUrl}/get-by-discipline/${discipline}?${queryParams.toString()}`)
      const { message, value } = response
      console.log("message: ", message)
      console.log("value: ", value)
      return value
    }
    catch (error) {
      console.error("Error al obtener los participantes:", error)
      throw error
    }
  }

  async getAllParticipantsByDiscipline(discipline: string) {
    try {
      const response = await this.httpClient.get<TBaseResponse<ParticipantList[]>>(`${this.baseUrl}/get-by-discipline/${discipline}`)
      const { message, value } = response

      console.log("message: ", message)
      return value
    }
    catch (error) {
      console.error("Error al obtener los participantes:", error)
      throw error
    }
  }

  async getBySportEvent(id: string) {
    try {
      const { data, error } = await tryCatch(this.httpClient.get<TBaseResponse<ParticipantList[]>>(`participant/by-sport-event/${id}?functionCode=ATHLE`))

      if (error) {
        console.error("Error al obtener el participante:", error)
        return null
      }
      return data
    }
    catch (error) {
      console.error("Error al obtener el participante:", error)
      throw error
    }
  }

  async syncParticipants(disciplineCode: string) {
    try {
      const response = await this.httpClient.get<TSyncEventsSummary>(`${this.baseUrl}/sync-from-gms/${disciplineCode}`)
      return response
    }
    catch (error) {
      console.error("Error al sincronizar los participantes:", error)
      throw error
    }
  }

  async deleteParticipant(participantId: string) {
    try {
      await this.httpClient.delete<void>(`${this.baseUrl}/delete/${participantId}`)
    }
    catch (error) {
      console.error("Error al borrar el participante")
      throw error
    }
  }

  async updateOfficials(unitCode: string, officials: { participantIds: string[] }) {
    try {
      const response = await this.httpClient.post<TBaseResponse<void>>(`result/update-officials/${unitCode}`, officials)
      return response
    }
    catch (error) {
      console.error("Error al actualizar los oficiales:", error)
      throw error
    }
  }

  async getOfficialsByUnitCode(unitCode: string) {
    const { data, error } = await tryCatch(this.httpClient.get<TBaseResponse<ParticipantList[]>>(`start-list/get-officials-by-unit-code/${unitCode}`))

    if (error) {
      console.error("Error al obtener los oficiales por unidad:", error)
      throw error
    }

    return data
  }
}

export const participantsService = ApiServiceFactory.createService(ParticipantsService, "participants")

export default ParticipantsService
